import { standingsGraphTeamOptions,
         getStandingsGraphTeamOptions,
         standingsGraphXAxisTimeScaleOptions,
         standingsGraphYAxisOptions,
         standingsGraphSeasonOptions,
         LOGO_ASSET_STATE_CHANGE_EVENT,
         getStandingsGraphDataFromSeasonData,
         getStandingsGraphOptions,
         getStandingsGraphOptionsBase,
         getDefaultTimeScaleForSeason,
   getDefaultSeason,
   preloadStandingsLogoAssets
        } from './standingsGraphData';

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Chart from 'chart.js/auto';
import { Line } from 'react-chartjs-2';
import { defaults } from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import 'chartjs-adapter-luxon';

import GraphOptions from './GraphOptions';
import { StandingsTable } from './Table';
import styled from 'styled-components';

import Layout from './Layout';


Chart.register(annotationPlugin);


const teamSubsetDefault = standingsGraphTeamOptions.all;
const seasonDefault = getDefaultSeason();
const yAxisDefault = standingsGraphYAxisOptions.record;

const StandingsContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  ${'' /* background-color: lightblue; */}

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-around;
    align-items: flex-start;
  }
`;

const TableContainer = styled.div`
  margin: 20px;
  width: 100%;
  height: auto;
  max-height: 80vh;
  overflow-x: auto;
  overflow-y: auto;
  display: inline-block;
  vertical-align: top;

  @media (min-width: 768px) {
    width: 50%;
  }
`;

const GraphContainer = styled.div`
  width: 100%;
  height: 80vh; // 80% of the viewport height
  overflow: hidden;
  display: inline-block;
  vertical-align: top;

  /* Feathered/blurred edge background */
  background: radial-gradient(circle at center,
    rgba(244, 244, 244, 1) 80%,
    rgba(244, 244, 244, 0.7) 90%,
    rgba(244, 244, 244, 0.3) 95%,
    rgba(244, 244, 244, 0) 100%
  );
  box-shadow: 0 0 32px 8px rgba(244, 244, 244, 0.3);
  border-radius: 16px;
  @media (min-width: 768px) {
    width: 50%;
    margin: 20px;
  }
`;

const GraphOptionsDiv = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  width: 100%;
  ${'' /* background-color: magenta; */}

  h1 {
    text-align: center;
  }
`;

const StandingsGraph = styled(Line)`
`

defaults.font.family = 'Inconsolata';
defaults.color = '#292929';
defaults.borderColor = 'rgba(0, 0, 0, 0.2)';
defaults.backgroundColor = 'rgba(0, 0, 0, 1)';

function Standings() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);

  // Parse and validate query parameters
  var urlSeason = queryParams.get('season');
  if (!urlSeason || !Object.values(standingsGraphSeasonOptions).includes(urlSeason)) {
    urlSeason = seasonDefault;
  }

  var urlTeams = queryParams.get('teams');
  if (!urlTeams) {
    urlTeams = teamSubsetDefault;
  }

  var urlGraphType = queryParams.get('graphType');
  if (!urlGraphType || !Object.values(standingsGraphYAxisOptions).includes(urlGraphType)) {
    urlGraphType = yAxisDefault;
  }

  var urlTimeScale = queryParams.get('timeScale');
  if (!urlTimeScale || !Object.values(standingsGraphXAxisTimeScaleOptions).includes(urlTimeScale)) {
    urlTimeScale = getDefaultTimeScaleForSeason(urlSeason);
  }


  const [seasonOption, setSeasonOption] = useState(urlSeason);
  const [teamSubsetOption, setTeamSubsetOption] = useState(urlTeams);
  const [yAxisOption, setYAxisOption] = useState(urlGraphType);
  const [timeScaleOption, setTimeScaleOption] = useState(urlTimeScale);

  const [seasonData, setSeasonData] = useState({});
  const [teamHistories, setTeamHistories] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [areLogoAssetsReady, setAreLogoAssetsReady] = useState(false);
  const [logoAssetVersion, setLogoAssetVersion] = useState(0);
  const chartRenderNonce = logoAssetVersion;

  const teamSubsetOptionsMap = useMemo(() => {
    return getStandingsGraphTeamOptions(seasonData.team_data);
  }, [seasonData]);

  useEffect(() => {
    if (seasonData.team_data === undefined) {
      return;
    }

    const validTeamSubsetValues = Object.values(teamSubsetOptionsMap).filter(value => value !== null);
    if (validTeamSubsetValues.length === 0) {
      return;
    }

    if (!validTeamSubsetValues.includes(teamSubsetOption)) {
      setTeamSubsetOption(standingsGraphTeamOptions.all);
    }
  }, [seasonData, teamSubsetOptionsMap, teamSubsetOption]);

  useEffect(() => {
    let isMounted = true;

    fetch('/json/teamHistories.json')
      .then(res => res.json())
      .then(
        result => {
          if (isMounted) {
            setTeamHistories(result);
          }
        },
        error => {
          console.log(error);
          if (isMounted) {
            setTeamHistories({});
          }
        }
      )

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const handleLogoAssetStateChange = () => {
      setLogoAssetVersion(version => version + 1);
    };

    window.addEventListener(LOGO_ASSET_STATE_CHANGE_EVENT, handleLogoAssetStateChange);
    return () => {
      window.removeEventListener(LOGO_ASSET_STATE_CHANGE_EVENT, handleLogoAssetStateChange);
    };
  }, []);

  useEffect(() => {
    const nextQueryParams = new URLSearchParams(location.search);
    nextQueryParams.set('season', seasonOption);
    nextQueryParams.set('teams', teamSubsetOption);
    nextQueryParams.set('graphType', yAxisOption);
    nextQueryParams.set('timeScale', timeScaleOption);

    const nextSearch = `?${nextQueryParams.toString()}`;
    if (nextSearch !== location.search) {
      navigate({
        search: nextQueryParams.toString()
      });
    }
  }, [location.search, navigate, teamSubsetOption, timeScaleOption, yAxisOption, seasonOption])

  useEffect(()=>{
    setIsLoading(true);
    fetch(`/nba/standings/${seasonOption}`)
      .then(res => res.json())
        .then(
          (result) => {
            setIsLoading(false);
            setSeasonData(result);
          },
          (error) => {
            setIsLoading(false);
            console.log(error);
          }
        )
  }, [seasonOption]);

  useEffect(() => {
    if (isLoading || teamHistories === null || seasonData.team_data === undefined) {
      setAreLogoAssetsReady(false);
      return;
    }

    let isMounted = true;

    preloadStandingsLogoAssets(seasonData.team_data, teamHistories, teamSubsetOption, seasonOption)
      .finally(() => {
        if (isMounted) {
          setAreLogoAssetsReady(true);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [isLoading, seasonData, teamHistories, teamSubsetOption, seasonOption]);

  const isPageLoading = isLoading || teamHistories === null || !areLogoAssetsReady;

  var data = useMemo(() => {
    if (isPageLoading) return {labels: [], datasets: []};
    const graphData = getStandingsGraphDataFromSeasonData(seasonData, teamHistories, teamSubsetOption, timeScaleOption, yAxisOption, seasonOption);
    graphData.renderNonce = chartRenderNonce;
    return graphData;
  }, [seasonData, teamHistories, teamSubsetOption, timeScaleOption, yAxisOption, isPageLoading, seasonOption, chartRenderNonce]);

  var options = useMemo(() => {
    if (isPageLoading) return getStandingsGraphOptionsBase();
    return getStandingsGraphOptions(data, seasonData.all_star_break, teamSubsetOption, timeScaleOption, yAxisOption, seasonOption)
  }, [data, seasonData, teamSubsetOption, timeScaleOption, yAxisOption, isPageLoading, seasonOption]);

  const xAxisTimeScaleOptions = { ...standingsGraphXAxisTimeScaleOptions };
  if (yAxisOption === standingsGraphYAxisOptions.seed) {
    // Remove 'game number' timescale option for seed graph
    if (timeScaleOption === standingsGraphXAxisTimeScaleOptions.gameNum) {
      setTimeScaleOption(xAxisTimeScaleOptions.day);
    }
    delete xAxisTimeScaleOptions.gameNum;
  }

  const teamSubsetOptions = {name: "teamSubsetOptions", options: teamSubsetOptionsMap, selected: teamSubsetOption, setter: setTeamSubsetOption};
  const timeScaleOptions = {name: "timeScaleOptions", options: xAxisTimeScaleOptions, selected: timeScaleOption, setter: setTimeScaleOption};
  const yAxisOptions = {name: "yAxisOptions", options: standingsGraphYAxisOptions, selected: yAxisOption, setter: setYAxisOption};
  const seasonOptions = {name: "seasonOptions", options: standingsGraphSeasonOptions, selected: seasonOption, setter: setSeasonOption};


  return (
    <Layout>
      <StandingsContent>
      {isPageLoading ? (
        <GraphContainer>Loading...</GraphContainer>
      ) : (
        <>
          <GraphContainer>
            <StandingsGraph data={data} options={options}/>
          </GraphContainer>
          <TableContainer>
            <StandingsTable data={data} teamSubset={teamSubsetOptions} numPlayoffTeams={seasonData.num_playoff_teams} numPlayInTeams={seasonData.num_play_in_teams}/>
          </TableContainer>
        </>
      )}
      </StandingsContent>
      <GraphOptionsDiv>
        <GraphOptions teamSubsetOptions={teamSubsetOptions}
                      timeScaleOptions={timeScaleOptions}
                      yAxisOptions={yAxisOptions}
                      seasonOptions={seasonOptions}/>
      </GraphOptionsDiv>
    </Layout>
  )
}

export default Standings;
